import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CountUp from "react-countup";
import { FiArrowRight, FiCheck, FiChevronUp, FiChevronDown } from "react-icons/fi";
import { industryDetails } from "../Data";
import { IoWarning } from "react-icons/io5";

const IndustryTabs = () => {
  const [activeIndustry, setActiveIndustry] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const current = industryDetails[activeIndustry];

  const contentRef = useRef(null);

  // Trigger scroll ONLY when a user clicks a tab button directly
  const handleTabClick = (index) => {
    setActiveIndustry(index);

    // Only scroll on screens >= 992px when manually clicked
    if (contentRef.current && window.innerWidth >= 992) {
      const elementPosition = contentRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY;
      const topOffset = 120;

      window.scrollTo({
        top: offsetPosition - topOffset,
        behavior: "smooth",
      });
    }
  };

  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 50,
      filter: "blur(10px)",
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const textVariant = {
    hidden: {
      opacity: 0,
      y: 25,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.25,
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const sidebarStagger = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const tabVariant = {
    hidden: {
      opacity: 0,
      x: -40,
      filter: "blur(8px)",
    },
    visible: {
      opacity: 1,
      x: 0,
      filter: "blur(0px)",
      transition: {
        duration: 0.55,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <>
     <section className="industry-detail-section container">
        {/* TOP SECTION */}
        <motion.div
          className="industry-detail-top"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2 className="section-heading" variants={fadeUp}>
            Automation Built for <span>Every Industry</span>
          </motion.h2>

          <motion.p className="section-text" variants={textVariant}>
            Explore industry-specific WhatsApp automation designed to streamline
            customer journeys, improve response times, and drive measurable
            business growth.
          </motion.p>
        </motion.div>

        <div className="industry-detail-wrapper">

<div className="industry-mobile-dropdown">

  <button
    className="industry-dropdown-btn"
    onClick={() => setDropdownOpen(!dropdownOpen)}
  >
    <div className="industry-dropdown-left">
      <img
        src={current.icon}
        alt={`${current.title} | Onbbits`}
      />

      <div>
        <h3>{current.title}</h3>
        <p>{current.flow}</p>
      </div>
    </div>

    {dropdownOpen ? <FiChevronUp /> : <FiChevronDown />}
  </button>

  <AnimatePresence>

    {dropdownOpen && (

      <motion.div
        className="industry-dropdown-menu"
        initial={{
          opacity:0,
          y:-10
        }}
        animate={{
          opacity:1,
          y:0
        }}
        exit={{
          opacity:0,
          y:-10
        }}
      >

        {industryDetails.map((item,index)=>(

          <button
            key={index}
            className={`industry-dropdown-item ${
              activeIndustry===index ? "active" : ""
            }`}
            onClick={()=>{
              setActiveIndustry(index);
              setDropdownOpen(false);
            }}
          >

            <img
              src={item.icon}
              alt={`${item.title} | Onbbits`}
            />

            <div>
              <h4>{item.title}</h4>
              <p>{item.flow}</p>
            </div>

          </button>

        ))}

      </motion.div>

    )}

  </AnimatePresence>

</div>


          {/* SIDEBAR TABS */}
          <motion.div
            className="industry-detail-sidebar desktop-sidebar"
            variants={sidebarStagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {industryDetails.map((item, index) => (
              <motion.button
                key={index}
                variants={tabVariant}
                className={`industry-detail-tab ${
                  activeIndustry === index ? "active" : ""
                }`}
                // Calling handleTabClick instead of direct setState
                onClick={() => handleTabClick(index)}
                whileHover={{
                  x: 8,
                  transition: { duration: 0.25 },
                }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="industry-detail-tab-left">
                  <motion.img
                    src={item.icon}
                    alt={`${item.title} | Onbbits`}
                    className="industry-detail-tab-icon"
                    whileHover={{ rotate: 8, scale: 1.08 }}
                    transition={{ duration: 0.3 }}
                  />
                  <div>
                    <h3 className="card-heading">{item.title}</h3>
                    <p className="section-text">{item.flow}</p>
                  </div>
                </div>

                <motion.div
                  animate={
                    activeIndustry === index ? { x: [0, 5, 0] } : {}
                  }
                  transition={{ duration: 1.2, repeat: Infinity }}
                >
                  <FiArrowRight />
                </motion.div>
              </motion.button>
            ))}
          </motion.div>

          {/* RIGHT CONTENT CARD */}
          <motion.div
            ref={contentRef}
            className="industry-detail-content"
            key={activeIndustry}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55 }}
          >
            {/* HEADER */}
            <motion.div
              className="industry-detail-header"
              initial={{ opacity: 0, y: -25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <div className="industry-detail-heading">
                <motion.div
                  className="industry-detail-icon"
                  initial={{ scale: 0.7, rotate: -15 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 220,
                    damping: 14,
                    delay: 0.2,
                  }}
                >
                  <img src={current.icon} alt={`${current.title} | Onbbits`} />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 25 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 }}
                >
                  <span className="industry-detail-subheading">
                    {current.flow}
                  </span>
                  <h3 className="card-heading">{current.title}</h3>
                  <p className="industry-detail-tagline">
                    {current.subText}
                  </p>
                </motion.div>
              </div>

              <motion.a
                href="https://app.onbbits.io/register"
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -4, scale: 1.04 }}
                transition={{ delay: 0.3 }}
              >
                Get Started
                <FiArrowRight />
              </motion.a>
            </motion.div>

            {/* STATS */}
            <motion.div
              className="industry-detail-stats"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: 0.12,
                    delayChildren: 0.35,
                  },
                },
              }}
            >
              {current.stats.map((item, index) => (
                <motion.div
                  className="industry-detail-stat"
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 35, scale: 0.9 },
                    visible: { opacity: 1, y: 0, scale: 1 },
                  }}
                  whileHover={{ y: -8 }}
                >
                  <h3 className="card-heading">
                    {item.prefix}
                    <CountUp end={item.value} duration={2} />
                    {item.suffix}
                  </h3>
                  <p className="section-text">{item.label}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* PROBLEM */}
            <motion.div
              className="problem-card"
              initial={{ opacity: 0, y: 35 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
            >
              <motion.div
                className="card-label problem-label"
              >
                <IoWarning />
                <span>PROBLEM</span>
              </motion.div>

              <motion.p
                className="section-text"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.75 }}
              >
                {current.problem}
              </motion.p>
            </motion.div>

            {/* BOTTOM */}
            <motion.div
              className="industry-detail-bottom"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              {/* WHY */}
              <motion.div
                className="industry-detail-why"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 }}
              >
                <h3 className="card-heading">Why ONBBITS?</h3>
                <div className="industry-detail-list">
                  {[
                    "98% open rate vs 20% for email",
                    "Customers already use it daily",
                    "Rich media - images, catalogs & buttons",
                    "End-to-end encrypted & trusted",
                  ].map((text, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1 + index * 0.08 }}
                      whileHover={{ x: 6 }}
                    >
                      <FiCheck />
                      <span className="section-text">{text}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* IMPACT */}
              <motion.div
                className="industry-detail-impact"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.95 }}
              >
                <h3 className="card-heading">Key Impacts</h3>
                <div className="industry-detail-impact-list">
                  {current.impacts.map((item, index) => (
                    <motion.div
                      className="industry-detail-impact-item"
                      key={index}
                      initial={{ opacity: 0, y: 25 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1 + index * 0.08 }}
                      whileHover={{ y: -5 }}
                    >
                      <span className="section-text">{item.title}</span>
                      <strong>{item.value}</strong>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default IndustryTabs;